import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSupabaseClient } from './supabaseClient';

const format12Hour = (timeStr) => {
  if (!timeStr) return '';
  const date = new Date(timeStr);
  let h = date.getHours();
  const minute = date.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  h = h ? h : 12;
  return `${h}:${minute} ${ampm}`;
};

const formatDate = (timeStr) => {
  const date = new Date(timeStr);
  return date.toISOString().split('T')[0];
};

const StatsModal = ({ show, onClose, loading, stats }) => {
  const maxCount = useMemo(() => {
    if (!stats || !stats.hourly) return 0;
    return Math.max(...stats.hourly.map(h => h.msg_count));
  }, [stats]);

  const getTimeLabel = (hour) => {
    const h = hour % 12 || 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${h}${ampm}`;
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div 
          className="global-stats-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div 
            className="global-stats-modal"
            initial={{ scale: 0.8, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 40 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Message Statistics</h3>
              <button className="close-modal" onClick={onClose}>✕</button>
            </div>
            
            {loading ? (
              <div className="loading-stats-container">
                <div className="heart-spinner">❤️</div>
                <p>Analyzing our patterns...</p>
              </div>
            ) : stats ? (
              <div className="stats-grid">
                <div className="stat-card total">
                  <span className="stat-label">Total Messages</span>
                  <span className="stat-value big">{stats.total.toLocaleString()}</span>
                </div>

                <div className="stat-row">
                  <div className="stat-card person">
                    <span className="stat-label">Sid</span>
                    <span className="stat-value">{stats.Sid.toLocaleString()}</span>
                    <span className="stat-sub">messages</span>
                  </div>
                  <div className="stat-card person">
                    <span className="stat-label">Hana</span>
                    <span className="stat-value">{stats.Hana.toLocaleString()}</span>
                    <span className="stat-sub">messages</span>
                  </div>
                </div>
                
                <div className="activity-chart-container">
                  <span className="stat-label">Activity by Time of Day</span>
                  <div className="hour-bars">
                    {stats.hourly.map((h) => (
                      <div key={h.hour_of_day} className="hour-bar-wrapper">
                        <div 
                          className="hour-bar" 
                          style={{ 
                            height: `${(h.msg_count / maxCount) * 100}%`,
                            opacity: 0.3 + (h.msg_count / maxCount) * 0.7
                          }}
                        >
                          <span className="bar-tooltip">{h.msg_count.toLocaleString()}</span>
                        </div>
                        <span className="hour-label">
                          {h.hour_of_day % 6 === 0 ? getTimeLabel(h.hour_of_day) : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="insight-card">
                  <p>
                    Our busiest hour is <strong>{getTimeLabel(stats.peakHour)}</strong>. 
                  </p>
                  <p className="final-verdict">
                    After thorough analysis, Sid definitely loves you more.
                  </p>
                </div>
              </div>
            ) : (
              <p>Couldn't load stats :(</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Messages = ({ onBack }) => {
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isHanaPerspective, setIsHanaPerspective] = useState(false);
  const [focusedId, setFocusedId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [supabaseClient, setSupabaseClient] = useState(null);
  
  // Stats state
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  
  const scrollRef = useRef(null);
  const PAGE_SIZE = 50;

  const fetchStats = async (client) => {
    setLoadingStats(true);
    const activeClient = client || supabaseClient;
    
    // Total counts by person
    const { count: sidCount } = await activeClient
      .from('memories')
      .select('*', { count: 'exact', head: true })
      .eq('sender', 'Sid');
      
    const { count: hanaCount } = await activeClient
      .from('memories')
      .select('*', { count: 'exact', head: true })
      .eq('sender', 'Hana');

    // Call the custom RPC function for hourly distribution
    const { data: hourlyData, error: rpcError } = await activeClient.rpc('get_hour_stats');
    
    if (!rpcError && hourlyData) {
      const fullHourly = Array.from({ length: 24 }, (_, i) => {
        const match = hourlyData.find(d => d.hour_of_day === i);
        return { hour_of_day: i, msg_count: match ? parseInt(match.msg_count) : 0 };
      });

      const peak = [...fullHourly].sort((a, b) => b.msg_count - a.msg_count)[0];

      setStats({
        Sid: sidCount || 0,
        Hana: hanaCount || 0,
        total: (sidCount || 0) + (hanaCount || 0),
        hourly: fullHourly,
        peakHour: peak ? peak.hour_of_day : 0
      });
    }
    
    setLoadingStats(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const client = getSupabaseClient(password);
    const { data, error: fetchError } = await client
      .from('memories')
      .select('id')
      .limit(1);

    if (fetchError || !data || data.length === 0) {
      setError('Wrong code, try again ❤️');
      setPassword('');
      setLoading(false);
    } else {
      setSupabaseClient(client);
      setIsAuthenticated(true);
      fetchInitialMessages(client);
      fetchStats(client);
    }
  };

  const fetchInitialMessages = async (client) => {
    const activeClient = client || supabaseClient;
    setLoading(true);
    const { data, error } = await activeClient
      .from('memories')
      .select('*')
      .order('time', { ascending: false })
      .limit(PAGE_SIZE);

    if (!error) {
      setMessages(data.reverse());
    }
    setLoading(false);
  };

  const loadMore = async () => {
    if (messages.length === 0 || !supabaseClient) return;
    setLoading(true);
    
    const oldestMessage = messages[0];
    const { data, error } = await supabaseClient
      .from('memories')
      .select('*')
      .lt('time', oldestMessage.time)
      .order('time', { ascending: false })
      .limit(PAGE_SIZE);

    if (!error && data.length > 0) {
      const scrollHeight = scrollRef.current.scrollHeight;
      const scrollTop = scrollRef.current.scrollTop;
      
      setMessages(prev => [...data.reverse(), ...prev]);
      
      setTimeout(() => {
        if (scrollRef.current) {
          const newScrollHeight = scrollRef.current.scrollHeight;
          scrollRef.current.scrollTop = scrollTop + (newScrollHeight - scrollHeight);
        }
      }, 0);
    }
    setLoading(false);
  };

  const [searchResults, setSearchResults] = useState([]);
  const [searchingData, setSearchingData] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (search.trim().length >= 2 && supabaseClient) {
        setSearchingData(true);
        const { data, error } = await supabaseClient
          .from('memories')
          .select('*')
          .ilike('message', `%${search}%`)
          .order('time', { ascending: false })
          .limit(50);
        
        if (!error) setSearchResults(data);
        setSearchingData(false);
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, supabaseClient]);

  const jumpToMessage = async (msg) => {
    if (!supabaseClient) return;
    setLoading(true);
    setSearch('');
    setIsSearching(false);
    
    const { data: before } = await supabaseClient
      .from('memories')
      .select('*')
      .lt('time', msg.time)
      .order('time', { ascending: false })
      .limit(20);
      
    const { data: after } = await supabaseClient
      .from('memories')
      .select('*')
      .gt('time', msg.time)
      .order('time', { ascending: true })
      .limit(20);

    const context = [...(before?.reverse() || []), msg, ...(after || [])];
    setMessages(context);
    setFocusedId(msg.id);
    setLoading(false);

    setTimeout(() => {
      const element = document.getElementById(`msg-${msg.id}`);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  };

  useEffect(() => {
    if (focusedId !== null && scrollRef.current) {
      const element = document.getElementById(`msg-${focusedId}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } else if (!loading && scrollRef.current && search === '' && !focusedId) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [loading, search, focusedId]);

  if (!isAuthenticated) {
    return (
      <motion.div className="messages-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="phone-hardware-wrapper">
          <div className="external-controls">
            <button className="external-back-btn" onClick={onBack}>← Back</button>
          </div>
          <div className="messages-container lock-screen">
            <div className="lock-content">
              <div className="lock-icon">🔒</div>
              <h3>Messages Locked</h3>
              <p>Enter our secret code to unlock 200,000+ messages</p>
              <form onSubmit={handleLogin}>
                <input 
                  type="password" 
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Secret code..."
                  className="lock-input"
                  autoFocus
                />
                {error && <p className="error-msg">{error}</p>}
                <button type="submit" className="unlock-btn" disabled={loading}>
                  {loading ? 'Verifying...' : 'Unlock'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="messages-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <StatsModal 
        show={showStats} 
        onClose={() => setShowStats(false)} 
        loading={loadingStats} 
        stats={stats} 
      />

      <div className="phone-hardware-wrapper">
        <div className="external-controls">
          <button className="external-back-btn" onClick={focusedId !== null ? () => { setFocusedId(null); fetchInitialMessages(); } : onBack}>
            {focusedId !== null ? '↺ Back' : '← Back'}
          </button>
          
          <div className="perspective-selector">
            <span>Viewing as:</span>
            <div className="toggle-group">
              <button className={!isHanaPerspective ? 'active' : ''} onClick={() => setIsHanaPerspective(false)}>Sid</button>
              <button className={isHanaPerspective ? 'active' : ''} onClick={() => setIsHanaPerspective(true)}>Hana</button>
            </div>
          </div>
        </div>

        <div className="messages-container">
          <div className="messages-header-modern">
            <div className="header-nav">
              <div className="placeholder-btn" />
              <div className="header-info">
                <h3>{focusedId !== null ? 'Messages' : 'Messages'}</h3>
              </div>
              <div className="header-actions">
                <button className={`icon-btn ${showStats ? 'active' : ''}`} onClick={() => setShowStats(true)}>
                  📊
                </button>
                <button className={`icon-btn ${isSearching ? 'active' : ''}`} onClick={() => setIsSearching(!isSearching)}>
                  🔍
                </button>
              </div>
            </div>
            
            <AnimatePresence>
              {isSearching && (
                <motion.div 
                  className="search-bar-container"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <div className="input-group">
                    <input 
                      autoFocus
                      type="text" 
                      placeholder="Search for a word or phrase..." 
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="modern-search-input"
                    />
                    {search && <button className="clear-search" onClick={() => setSearch('')}>✕</button>}
                  </div>
                  
                  {search.trim().length >= 2 && (
                    <div className="modern-results-dropdown">
                      {searchingData ? (
                        <div className="no-matches-msg">Searching...</div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((m) => (
                          <div key={m.id} className="modern-result-item" onClick={() => jumpToMessage(m)}>
                            <div className="result-meta">
                              <span className="name">{m.sender}</span>
                              <span className="dot">•</span>
                              <span className="date">{formatDate(m.time)}</span>
                            </div>
                            <p className="text">{m.message}</p>
                          </div>
                        ))
                      ) : (
                        <div className="no-matches-msg">No memories found with "{search}"</div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="messages-list-modern" ref={scrollRef}>
            <div className="bubbles-wrapper">
              {focusedId === null && !loading && (
                <button className="load-older-btn" onClick={loadMore}>Load earlier messages</button>
              )}
              
              {messages.map((m, i) => {
                const isMe = m.sender === 'Sid';
                const isRightSide = isHanaPerspective ? !isMe : isMe;
                const showDate = i === 0 || formatDate(m.time) !== formatDate(messages[i-1].time);
                
                return (
                  <React.Fragment key={m.id}>
                    {showDate && <div className="date-separator">{formatDate(m.time)}</div>}
                    <div 
                      id={`msg-${m.id}`}
                      className={`msg-row ${isRightSide ? 'me' : 'them'} ${focusedId === m.id ? 'is-focused' : ''}`}
                    >
                      <div className="msg-bubble-modern">
                        <p>{m.message}</p>
                        <span className="msg-time-modern">{format12Hour(m.time)}</span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              {loading && (
                <div className="sync-loader-inline">
                  <p>Loading...</p>
                </div>
              )}
              <div className="bottom-spacing" />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Messages;
